// Was previously the part of src/classes/base.ts that retrieved data on the
// campaign and saved it to the store. This is now handled by the mixin.
// The part of base.ts that handled prop passing is now "MCampaignWidgetPropsProvider.ts"
import { mixins, Options } from 'vue-class-component';
import { createStore } from 'vuex';
import { setProviderEventsHandler } from '@manifoldxyz/manifold-dropsitetools-lib';
import { EthereumProvider } from '@manifoldxyz/manifold-sdk';
import { RedemptionResult, ShopifySubmissionResult, Token } from '@/api/models';
import RedeemServerClient, { CampaignAction } from '@/api/redeem/redeem';
import { MCampaignWidgetInjectContext } from '@/exports/MCampaignWidgetProps';

@Options<CampaignWidgetMixin>({
  name: 'CampaignWidgetMixin',
  watch: {
    'state.leadingComponent': function onLeadingComponentChanged(value: any) {
      if (!value) {
        this.registerLeadingComponent();
      }
    }
  }
})

// This replaces the old BaseClass (along with the PropProvider) but handles the logic for
// fetching info about the campaign and saving those to the store.
export default class CampaignWidgetMixin extends mixins(MCampaignWidgetInjectContext) {
  get state() {
    if (this.mmid && this.$store) {
      return this.$store.state[this.mmid];
    } else {
      return createStore({});
    }
  }

  async created() {
    this.mmid = `${this.campaignId}`;

    this.$watch('state.leadingComponent', this.onLeadingComponent);
    this.registerLeadingComponent();

    setProviderEventsHandler(this.initialize);
    window.addEventListener('m-authenticated', this.initialize);

    const provider = EthereumProvider.provider();
    if (!provider) {
      // No provider connected yet, so attempt to initialize connection, default  to mainnet
      // TODO change this to the network passed in
      // await EthereumProvider.initialize(this.network || EthereumNetwork.MAINNET);
      await EthereumProvider.initialize(this.network);
    }

    if (EthereumProvider.selectedAddress()) {
      await this.initialize();
    }
  }

  async mounted() {
    if (this.state.leadingComponent === this) {
      this.$store.commit(`${this.mmid}/setRedeemAPIClient`, new RedeemServerClient(), {
        root: true
      });
      await this.getCampaignData();
    }
  }

  interval() {
    setTimeout(() => {
      this.$store.dispatch(`${this.mmid}/updateCountdown`, { root: true });
      requestAnimationFrame(this.interval);
    }, 1000);
  }

  async getCampaignData() {
    this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });

    const campaign = await this.state.RedeemAPIClient.getCampaign(parseInt(this.mmid!));

    this.$store.commit(`${this.mmid}/setCampaign`, campaign, { root: true });
    this.$store.commit(`${this.mmid}/setCampaignStartTime`, campaign.startDate, { root: true });
    this.$store.commit(`${this.mmid}/setCampaignEndTime`, campaign.endDate, { root: true });
    requestAnimationFrame(this.interval);

    switch (campaign.action!) {
      case CampaignAction.QUESTION:
        this.$store.commit(`${this.mmid}/setCampaignActionType`, CampaignAction.QUESTION, {
          root: true
        });
        this.$store.commit(
          `${this.mmid}/setQuestionsForCampaign`,
          await this.state.RedeemAPIClient.getCampaignQuestions(parseInt(this.mmid!)),
          { root: true }
        );
        break;
      case CampaignAction.SHOPIFY_DISCOUNT:
        this.$store.commit(`${this.mmid}/setCampaignActionType`, CampaignAction.SHOPIFY_DISCOUNT, {
          root: true
        });
        break;
      case CampaignAction.SHOPIFY_URL:
        this.$store.commit(`${this.mmid}/setCampaignActionType`, CampaignAction.SHOPIFY_URL, {
          root: true
        });
        this.$store.commit(
          `${this.mmid}/setCampaignShopifyProducts`,
          await this.state.RedeemAPIClient.getCampaignShopifyProducts(parseInt(this.mmid!)),
          { root: true }
        );
        break;
      case CampaignAction.SHOPIFY_DELAYED:
        this.$store.commit(`${this.mmid}/setCampaignActionType`, CampaignAction.SHOPIFY_DELAYED, {
          root: true
        });
        this.$store.commit(
          `${this.mmid}/setCampaignShopifyProducts`,
          await this.state.RedeemAPIClient.getCampaignShopifyProducts(parseInt(this.mmid!)),
          { root: true }
        );
        break;
      case CampaignAction.CODE:
        this.$store.commit(`${this.mmid}/setCampaignActionType`, CampaignAction.CODE, {
          root: true
        });
        this.$store.commit(
          `${this.mmid}/setCampaignCodeOptions`,
          await this.state.RedeemAPIClient.getCampaignCodeOptions(parseInt(this.mmid))
        );
        break;
      case CampaignAction.CODE_WITH_QUESTIONS:
        // Set the state.camapignActionType to CODE_WITH_QUESTIONS (6)
        this.$store.commit(
          `${this.mmid}/setCampaignActionType`,
          CampaignAction.CODE_WITH_QUESTIONS,
          {
            root: true
          }
        );
        // Fetch the options for the campaign
        this.$store.commit(
          `${this.mmid}/setCampaignCodeOptions`,
          await this.state.RedeemAPIClient.getCampaignCodeOptions(parseInt(this.mmid))
        );
        // Fetch the questions on the campaign
        this.$store.commit(
          `${this.mmid}/setQuestionsForCampaign`,
          await this.state.RedeemAPIClient.getCampaignQuestions(parseInt(this.mmid!)),
          { root: true }
        );
        break;
      default:
        throw Error('Campaign Action undefined');
    }
    await this.updateState();

    this.$store.commit(`${this.mmid}/setCampaignTitle`, campaign.name, { root: true });
    this.$store.commit(`${this.mmid}/setLoaded`, true, { root: true });
  }

  onLeadingComponent(value: any, _oldValue: any) {
    if (!value) {
      this.registerLeadingComponent();
    }
  }

  // ** NOTE: Vue3 beforeDestroy() is now called beforeUnmount()
  beforeUnmount() {
    if (this.state.leadingComponent === this) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, undefined, { root: true });
    }
  }

  registerLeadingComponent() {
    if (!this.$store.state[this.mmid].leadingComponent) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, this, { root: true });
    }
  }

  // MRedemption view-related states/functions

  clearActiveTokenAndRedemption() {
    this.$store.commit(`${this.mmid}/setActiveEligibleToken`, null, { root: true });
    this.$store.commit(`${this.mmid}/setActiveRedemption`, null, { root: true });
  }

  /**
   * Sets the active redemption to {{result}} and the active eligible token to
   * null. Called when redeeming a token (transitioning from a token -> redemption)
   * @param result
   */
  setRedemptionDetails(result: RedemptionResult) {
    this.$store.commit(`${this.mmid}/setActiveRedemption`, result, { root: true });
    this.$store.commit(`${this.mmid}/setActiveEligibleToken`, null, { root: true });
  }

  /**
   * Sets the active eligible token depending on the active redemption. Called
   * when modifying a redemption (transitioning from redemption -> token).
   * Keeps the current active redemption. If there is no active redemption,
   * does nothing.
   */
  async modifyCurrentRedemption() {
    // sanity check
    if (!this.state.activeRedemption) {
      return;
    }
    // checking redemptions
    const oauthToken = await this.getOAuth();
    const redemptions = await this.state.RedeemAPIClient.getRedemptions(
      oauthToken!,
      parseInt(this.mmid)
    );
    const updatedCurrentRedemption = redemptions.find(
      (redemption: RedemptionResult) => redemption.id == this.state.activeRedemption.id
    );
    if (!updatedCurrentRedemption) {
      console.error(
        'Redemptions do not include current active redemption. Please reload the page.'
      );
      return;
    }

    this.$store.commit(`${this.mmid}/setActiveRedemption`, updatedCurrentRedemption, {
      root: true
    });

    const currentCompleted =
      (updatedCurrentRedemption.submissions?.[0] as ShopifySubmissionResult)?.completed ?? false;

    if (currentCompleted) {
      // actual change happened
      alert(
        'Sorry; the redemption cannot be modified anymore as the Shopify order has already been completed.'
      );
      return;
    }

    const tokenToMod = this.state.tokenData.find((token: Token) => {
      return (
        token.contractAddress === this.state.activeRedemption.contractAddress &&
        token.tokenId === this.state.activeRedemption.tokenId
      );
    });

    this.$store.commit(`${this.mmid}/setActiveEligibleToken`, tokenToMod, { root: true });
  }

  async initialize(_event?: any) {
    if (this.state.leadingComponent === this) {
      const handling = this.state.isHandlingProviderEvent;
      const connectedAddress = EthereumProvider.selectedAddress();

      if (!handling.status || (!handling.address && connectedAddress) || connectedAddress) {
        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: true, address: connectedAddress },
          { root: true }
        );
        await this.updateState();
        this.$store.commit(`${this.mmid}/setWrongChain`, !EthereumProvider.chainIsCorrect(), {
          root: true
        });

        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: false, address: connectedAddress },
          { root: true }
        );
      }
    }
  }

  /**
   * If force = true:
   *
   * - we clear the wallet and token data if no connected address;
   * - we clear the wallet and token data if no auth token
   * - we try to get shopify queue status every 5 s if campaign action type = shopify_delayed
   * - we set the user's token data and wallet address by refetching from the redeem server
   */
  async updateState(force = false) {
    if (force || this.state.leadingComponent === this) {
      // Start loading
      if (force) {
        this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });
      }
      const loading = this.state.loading;
      this.$store.commit(`${this.mmid}/setLoading`, true, { root: true });
      try {
        const connectedAddress = EthereumProvider.selectedAddress();

        // Clear redemption info if disconnected
        if (!connectedAddress) {
          this.clearState();
          return;
        }

        const oauthToken = await this.getOAuth(true);

        if (!oauthToken) {
          // No OAuth token, not fully connected yet, no data available, clear redemption info
          this.clearState();
          return;
        }

        // ---------------------
        // Load campaign details
        // ---------------------

        if (!this.state.campaign && !loading && this.state.RedeemAPIClient) {
          // Only need to load if not yet loaded or another thread is not loading the campaign data
          // Get campaigns for current product
          const campaign = await this.state.RedeemAPIClient.getCampaign(parseInt(this.mmid!));
          this.$store.commit(`${this.mmid}/setCampaign`, campaign, { root: true });
        }

        // For delayed queue, we retry the queue status every 5 seconsd
        if (this.state.campaign?.action == CampaignAction.SHOPIFY_DELAYED) {
          setInterval(async () => {
            this.$store.commit(
              `${this.mmid}/setShopifyQueue`,
              await this.state.RedeemAPIClient.getShopifyQueue(oauthToken, parseInt(this.mmid!)),
              { root: true }
            );
          }, 5000);
        }

        // -------------------------
        // End Load campaign details
        // -------------------------

        // Detect if wallet address has changed and set the wallet address to prevent race conditions
        const walletAddressChanged = this.state.walletAddress !== connectedAddress;
        this.$store.commit(`${this.mmid}/setWalletAddress`, connectedAddress, { root: true });

        // ----------------------------
        // Load eligible tokens details
        // ----------------------------

        if (force || !this.state.userInfoLoaded || walletAddressChanged) {
          this.$store.commit(`${this.mmid}/setUserInfoLoaded`, true, { root: true });
          try {
            const tokens = await this.state.RedeemAPIClient.getEligibleTokens(
              oauthToken,
              parseInt(this.mmid!)
            );
            for (const token of tokens) {
              token.image = token.image?.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
              token.image = token.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            this.$store.commit(`${this.mmid}/setTokensForCampaign`, tokens, { root: true });
            const redemptions = await this.state.RedeemAPIClient.getRedemptions(
              oauthToken,
              parseInt(this.mmid!)
            );
            let redemptionsByOthers: RedemptionResult[] = [];
            if (!this.state.campaign?.snapshot) {
              redemptionsByOthers = await this.state.RedeemAPIClient.getRedemptionsByOthers(
                oauthToken,
                parseInt(this.mmid!)
              );
            }
            for (const redemption of redemptions) {
              redemption.image = redemption.image?.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
              redemption.image = redemption.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            for (const redemption of redemptionsByOthers) {
              redemption.image = redemption.image?.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
              redemption.image = redemption.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            this.$store.commit(`${this.mmid}/setAlreadyRedeemed`, redemptions, { root: true });
            this.$store.commit(`${this.mmid}/setAlreadyRedeemedByOthers`, redemptionsByOthers, {
              root: true
            });
            this.$store.commit(`${this.mmid}/setWalletAddress`, connectedAddress, { root: true });
          } catch (error) {
            // Error, unload user info
            this.clearState();
          }
        }

        // ---------------------------------
        // Done load eligible tokens details
        // ---------------------------------
      } finally {
        this.$store.commit(`${this.mmid}/setLoaded`, true, { root: true });
        this.$store.commit(`${this.mmid}/setLoading`, false, { root: true });
      }
    }
  }

  /**
   * Helper function to clear state
   */
  async clearState() {
    this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });
    this.$store.commit(`${this.mmid}/setWalletAddress`, undefined, { root: true });
    this.$store.commit(`${this.mmid}/setTokensForCampaign`, [], { root: true });
    this.$store.commit(`${this.mmid}/setAlreadyRedeemed`, [], { root: true });
    this.$store.commit(`${this.mmid}/setAlreadyRedeemedByOthers`, [], { root: true });
    this.$store.commit(`${this.mmid}/setUserInfoLoaded`, false, { root: true });
  }

  getOAuth(delayAuth = false) {
    return EthereumProvider.getOAuth({
      grantType: this.$store.state[this.mmid].grantType,
      appName: this.$store.state[this.mmid].appName,
      clientId: this.$store.state[this.mmid].clientId,
      strictAuth: true,
      delayAuth: delayAuth
    });
  }

  get getters() {
    return {
      campaignIsActive: this.$store.getters[`${this.mmid}/campaignIsActive`],
      countdown: this.$store.getters[`${this.mmid}/countdown`],
      countdownType: this.$store.getters[`${this.mmid}/countdownType`],
      walletConnected: this.$store.getters[`${this.mmid}/walletConnected`],
      campaignCodeOptionList: this.$store.getters[`${this.mmid}/campaignCodeOptionList`]
    };
  }
}
